import React, { useEffect, useState } from "react";
import { Check, X, Mail, MapPin, Phone, Pause, Play, Coins, ExternalLink, MessageCircle, ChevronDown, Search, UserPlus } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { PartnerInviteCard } from "@/components/PartnerInviteCard";
import { StatusPill } from "@/components/VenueBits";
import { AccountActionDialog, AccountAction } from "@/components/AccountActionDialog";
import { api } from "@/lib/api";
import { fmtDateTime, fmtDate, fmtDay } from "@/lib/time";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { VenueAccount, User } from "@/types";
import { useLive } from "@/lib/live";

const ACTIONS: Record<string, { a: AccountAction; icon: any; cls: string }[]> = {
  pending: [{ a: "approve", icon: Check, cls: "bg-emerald-500 text-white" }, { a: "reject", icon: X, cls: "border" }],
  rejected: [{ a: "approve", icon: Check, cls: "bg-emerald-500 text-white" }],
  approved: [{ a: "suspend", icon: Pause, cls: "border text-rose-600" }, { a: "fee", icon: Coins, cls: "border" }],
  suspended: [{ a: "reactivate", icon: Play, cls: "bg-emerald-500 text-white" }, { a: "fee", icon: Coins, cls: "border" }],
};

const FeeLine = ({ a }: { a: VenueAccount }) => {
  const { t, lang } = usePrefs();
  const fee = a.fee_current;
  const main = fee && fee.model === "free_trial" ? `${t("fm.free_trial")} · ${t("admin.trialEnds").replace("{date}", fmtDay(((fee as any).trial_end || "").slice(0, 10), lang))}`
    : fee && fee.model && fee.model !== "none" ? `${fee.per_person.amount} ${fee.per_person.currency} · ${t(`fm.${fee.model}`)}` : t("admin.noFee");
  const exempt = fee?.exempt && (!fee.exempt_until || new Date(fee.exempt_until) > new Date());
  return (
    <span data-testid={`account-fee-${a.account_id}`} className="inline-flex flex-wrap items-center gap-1 min-w-0">
      <Coins className="h-3.5 w-3.5 shrink-0" /><span dir="auto">{main}</span>
      {exempt && <span data-testid={`account-exempt-${a.account_id}`} className="shrink-0 rounded-full bg-emerald-500/15 text-emerald-600 px-1.5 font-bold">{t("pc.exempt")}</span>}
    </span>
  );
};

const AccountCard = ({ a, isSuper, onAct }: { a: VenueAccount; isSuper: boolean; onAct: (x: AccountAction) => void }) => {
  const { t, lang } = usePrefs();
  const [open, setOpen] = useState(false);
  const fee = a.fee_current;
  const wa = a.phone && `https://wa.me/${a.phone.replace(/\D/g, "")}?text=${encodeURIComponent(t("admin.waMsg").replace("{name}", a.business_name))}`;
  return (
    <div data-testid={`venue-account-${a.account_id}`} className="rounded-2xl border bg-card min-w-0">
      <div className="p-3 flex items-center gap-3">
        <div className="h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary grid place-items-center font-display text-lg font-black">{a.business_name.trim().charAt(0)}</div>
        <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`account-toggle-${a.account_id}`} className="flex-1 min-w-0 text-start">
          <p className="font-bold truncate">{a.business_name}</p>
          <p className="text-xs text-muted-foreground truncate">{a.governorate ? t(`gov.${a.governorate}`) : a.city}{a.area && ` · ${a.area}`}</p>
        </button>
        <StatusPill status={a.status} />
        <ChevronDown onClick={() => setOpen(!open)} className={`h-4 w-4 shrink-0 text-muted-foreground cursor-pointer transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      <p className="px-3 pb-2 text-xs text-muted-foreground"><FeeLine a={a} /></p>
      <div className="px-3 pb-3 flex flex-wrap items-center gap-1.5">
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" data-testid={`whatsapp-account-${a.account_id}`} aria-label={t("admin.whatsapp")}
            className="h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 bg-[#25D366] text-white hover:bg-[#1ebe5b] transition-colors">
            <MessageCircle className="h-4 w-4" />{t("pc.wa")}
          </a>
        )}
        {isSuper && (ACTIONS[a.status] || []).map(({ a: act, icon: Icon, cls }) => (
          <button key={act} data-testid={`${act}-account-${a.account_id}`} onClick={() => onAct(act)} className={`h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 ${cls}`}>
            <Icon className="h-4 w-4" />{t(act === "fee" ? "admin.changeFee" : `admin.${act}`)}
          </button>
        ))}
      </div>
      {open && (
        <div data-testid={`account-details-${a.account_id}`} className="border-t px-3 py-3 space-y-2 text-xs">
          <div className="grid gap-1 text-muted-foreground">
            {a.email && <span className="inline-flex items-center gap-1.5" dir="ltr"><Mail className="h-3.5 w-3.5" />{a.email}</span>}
            {a.phone && <span className="inline-flex items-center gap-1.5" dir="ltr"><Phone className="h-3.5 w-3.5" />{a.phone}</span>}
            {(a.address || a.area) && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{[a.area, a.address].filter(Boolean).join(" · ")}</span>}
            {a.maps_url && <a href={a.maps_url} target="_blank" rel="noreferrer" data-testid={`account-maps-${a.account_id}`} className="inline-flex items-center gap-1.5 text-primary font-bold"><ExternalLink className="h-3.5 w-3.5" />{t("admin.maps")}</a>}
          </div>
          {fee && (
            <p className="text-muted-foreground">
              {t("admin.effective")} {fmtDate(fee.effective_from, lang)}
              {fee.exempt && ` · ${!fee.exempt_until ? t("admin.exemptForever") : new Date(fee.exempt_until) > new Date()
                ? t("admin.exemptUntil").replace("{date}", fmtDate(fee.exempt_until, lang)) : t("admin.exemptEnded").replace("{date}", fmtDate(fee.exempt_until, lang))}`}
            </p>
          )}
          {a.status_history && a.status_history.length > 0 && (
            <div data-testid={`account-history-${a.account_id}`}>
              <p className="font-bold mb-1">{t("admin.history")}</p>
              <ul className="space-y-1">
                {[...a.status_history].reverse().map((h, i) => (
                  <li key={i} className="rounded-lg bg-muted/60 px-2.5 py-1"><b>{t(`status.${h.status}`)}</b> · {h.by_name} · {fmtDateTime(h.at, lang)}{h.reason && ` — ${h.reason}`}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FILTERS = ["all", "pending", "approved", "suspended", "rejected"] as const;

export default function AdminVenues() {
  const { t } = usePrefs();
  const me = useAuth().user as User;
  const isSuper = (me.admin_role || "super") === "super";
  const [list, setList] = useState<VenueAccount[] | null>(null);
  const [act, setAct] = useState<{ id: string; a: AccountAction } | null>(null);
  const [q, setQ] = useState("");
  const [st, setSt] = useState<(typeof FILTERS)[number]>("all");
  const load = () => api.get("/admin/venue-accounts").then((r) => setList(r.data)).catch(() => setList([]));
  useLive(() => load());
  useEffect(() => {
    load();
  }, []);

  const needle = q.trim().toLowerCase();
  const shown = list?.filter((a) => (st === "all" || a.status === st) && (!needle || [a.business_name, a.phone, a.email, a.area, a.governorate && t(`gov.${a.governorate}`)].some((x) => x && String(x).toLowerCase().includes(needle))));
  return (
    <div data-testid="admin-venues-page">
      <PageTitle title={t("admin.venuesTitle")} sub={isSuper ? t("admin.venuesSub") : t("admin.superOnly")} />
      <details className="mb-4 group" data-testid="partner-invite-toggle">
        <summary className="cursor-pointer list-none h-11 px-4 rounded-full border bg-card inline-flex items-center gap-2 text-sm font-bold"><UserPlus className="h-4 w-4 text-primary" />{t("pc.invite")}<ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" /></summary>
        <div className="mt-3"><PartnerInviteCard /></div>
      </details>
      <div className="space-y-2 mb-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pc.search")} data-testid="partners-search" className="h-11 w-full rounded-full border bg-card ps-9 pe-4 text-sm" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => {
            const n = f === "all" ? list?.length || 0 : (list || []).filter((x) => x.status === f).length;
            return (
              <button key={f} onClick={() => setSt(f)} aria-pressed={st === f} data-testid={`partners-filter-${f}`}
                className={`h-8 px-3 shrink-0 rounded-full text-xs font-bold ${st === f ? "bg-foreground text-background" : "bg-card border"}`}>{t(f === "all" ? "pc.all" : `status.${f}`)} {n}</button>
            );
          })}
        </div>
      </div>
      {shown?.length === 0 && <p className="text-muted-foreground py-10 text-center">{t("admin.empty")}</p>}
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
        {shown?.map((a) => <AccountCard key={a.account_id} a={a} isSuper={isSuper} onAct={(x) => setAct({ id: a.account_id, a: x })} />)}
      </div>
      {act && <AccountActionDialog key={`${act.id}-${act.a}`} accountId={act.id} action={act.a} onClose={() => setAct(null)} onDone={load} />}
    </div>
  );
}
